import React, { useEffect, useState, useMemo, useContext } from 'react';
import Spinner from '../../components/shared/Spinner';
import {
  getInboxMetadata,
  getInboxFiles,
  getValueByNestedKey,
  updateInboxItemStatus,
  isEmployee,
  deleteApi
} from './utils';
import InboxTable from './InboxTable';
import DownloadMultiple from './DownloadMultiple';
import { metadataDetails, getColumns } from './inboxConfig';
import USBBreadcrumb from '@usb-shield/components-react/dist/cjs/breadcrumb';
import { FeatureFlagsContext } from '../../components/Hooks/useFeatureFlags';
import RequirementNotFound from '../_requirementNotFound';
import DeleteMultiple from './DeleteMultiple';
import useUserRole from '../../components/Hooks/use-UserRole';

import {
  USBButton,
} from '@usb-shield/components-react';
import USBModal, { ModalHeader, ModalBody, ModalFooter } from '@usb-shield/react-modal';
import Messages from '../..//components/organisms/Messages/Messages';

export default function InboxDetails() {
  const { featureDetails } = useContext(FeatureFlagsContext);
  const [inboxMetadata, setInboxMetadata] = useState({});
  const [status, setStatus] = useState({ loading: true, error: null });
  const [files, setFiles] = useState([]);
  const [noDataError, setNoDataError] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState(/** @type {any[]} */([]));
  const [isDeleting, setIsDeleting] = useState(false);
  const [successFlag, setSuccessFlag] = useState(false);
  const [errorFlag, setErrorFlag] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const tempUserRole = useUserRole();
  const isDeleteAllowed = tempUserRole && tempUserRole === 'employee' && (!sessionStorage.getItem('remote_user'));

  async function fetchData() {
    await getInboxMetadata({ setInboxMetadata, setStatus, setNoDataError });
    await getInboxFiles({ setFiles, setStatus });
    await setStatus({ loading: false, error: status.error });
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (!isEmployee()) {
      updateInboxItemStatus();
    }
  }, [inboxMetadata]);

  useEffect(() => {
    if (featureDetails?.length > 0) {
      if (!featureDetails?.includes("feature_inbox_visible")) {
        window.location.href = "/Redirect";
      }
    }
  }, [featureDetails]);

  const requirementId = useMemo(() => {
    const url = window.location.pathname;
    return url.substring(url.lastIndexOf('/') + 1);
  }, []);

  /** @param {any[]} rows */
  const openDeleteModal = (rows = []) => {
    if (!rows.length) return;
    setDeleteTargets(rows);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteTargets([]);
  };

  const handleClose = (setFailedFlag, setSuccessFlag) => {
    setFailedFlag(false);
    setSuccessFlag(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargets.length) return;
    setIsDeleting(true);
    try {
      await deleteApi(deleteTargets, requirementId, setSuccessFlag, setErrorFlag, setMessageContent);
      await getInboxFiles({ setFiles, setStatus });
    } finally {
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  const tableColumns = useMemo(
    () => getColumns({ onDeleteRequest: openDeleteModal }),
    []
  );

  /** @param {{ selectedRows: any[] }} props */
  const DeleteActionWithModal = ({ selectedRows }) => (
    <DeleteMultiple
      selectedRows={selectedRows}
      onDeleteRequest={openDeleteModal}
    />
  );

  const totalNumberOfFiles = files?.length || 0;

  if (noDataError) {
    return <RequirementNotFound />;
  }

  return (
    <>
      {featureDetails?.length > 0 && featureDetails?.includes("feature_inbox_visible") ?
        <section className="inbox-details-container">
          {status.loading ? <Spinner /> : null}

          <USBBreadcrumb
            id="inbox"
            text="Inbox"
            breadcrumbs={[
              {
                id: 1,
                text: 'Inbox',
                href: '/Inbox',
              },
              {
                id: 2,
                text: 'Inbox Details'
              },
            ]}
          />

          <USBModal id="inbox-delete-modal" addClasses="delete-modal" isOpen={deleteModalOpen} handleClose={closeDeleteModal}>
            <ModalHeader id="inbox-delete-modal-header">Delete File</ModalHeader>
            <ModalBody>
              <div className="errormodalbody">
                <div className="deletetextframe">
                  Are you sure you want to delete
                  {deleteTargets.length > 1 ? ' these files?' : ' this file?'}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="deletebtnmodalframe">
                <span className="cancel-btn">
                  <USBButton id="cancelButton" handleClick={closeDeleteModal}>
                    Cancel
                  </USBButton>
                </span>
                <USBButton
                  variant="primary"
                  id="deleteDocument"
                  handleClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </USBButton>
              </div>
            </ModalFooter>
          </USBModal>
          <h1 className="heading-h1 p-t-md">Inbox Details</h1>
          <p className="secondary-text p-t-xs p-b-md">
            You'll see files and details related to the selected inbox document.
            Select a file to view or download.
          </p>
          <section className="inbox-metadata-container m-t-lg m-b-lg">
            {metadataDetails.map((metadataItem) => (
              <section className="metadata-item" key={metadataItem.key}>
                <p className="metadata-title"> {metadataItem.title}</p>
                <p className="metadata-value">
                  {getValueByNestedKey(inboxMetadata, metadataItem.key)}
                </p>
              </section>
            ))}
          </section>
          {successFlag || errorFlag ? (
            <div
              className="message-content"
              data-testid="messageContent"
              // role="alert"
            >
              <Messages
                success={successFlag}
                closeMessage={handleClose}
                singleUploadFail={errorFlag}
                messageContent={messageContent}
                setFailedFlag={setErrorFlag}
                setSuccessFlag={setSuccessFlag}
                severity={'high'}
              />
            </div>
          ) : (
            ''
          )}
          <p className="p-t-lg">
            Total files for this document: {totalNumberOfFiles}
          </p>

          {/* <section className="m-t-lg"> */}
          <InboxTable
            data={files || []}
            headers={tableColumns}
            status={status}
            setStatus={setStatus}
            searchProperties={{
              enable: true,
              keys: [
                'documentMetadata.documentName',
                'documentMetadata.uploadDate',
              ],
            }}
            tableActions={{
              enable: true,
              selectionKey: 'documentMetadata',
              Components: [DownloadMultiple],
            }}
          ></InboxTable>
          {/* </section> */}
        </section>
        : ""}
    </>
  );
}
